/**
 * Cómo se muestran los números en MGB.
 *
 * La plata llega desde la API en centavos, como número entero grande, y acá
 * se convierte a texto. La cuenta NO se hace acá: esto sólo dibuja. Si algún
 * día hay que sumar en el navegador, se suma en centavos con BigInt, nunca
 * con números con coma.
 *
 * Formato argentino: punto para los miles, coma para los decimales.
 */

/** Agrupa de a tres con puntos: 1400000 -> "1.400.000" */
function conPuntos(digitos) {
  let salida = '';
  for (let i = 0; i < digitos.length; i++) {
    if (i > 0 && (digitos.length - i) % 3 === 0) salida += '.';
    salida += digitos[i];
  }
  return salida;
}

function partirCentavos(centavos) {
  const n = BigInt(centavos);
  const negativo = n < 0n;
  const abs = negativo ? -n : n;
  return {
    negativo,
    pesos: (abs / 100n).toString(),
    centavos: (abs % 100n).toString().padStart(2, '0'),
  };
}

/**
 * La plata con sus centavos, para tablas de detalle y para todo lo que
 * tenga que conciliar: "$ 1.400.000,00", "-$ 150.000,50".
 */
export function formatearPesos(centavos) {
  const p = partirCentavos(centavos);
  return `${p.negativo ? '-' : ''}$ ${conPuntos(p.pesos)},${p.centavos}`;
}

/**
 * La plata redondeada al peso, para los números grandes del inicio:
 * "$ 1.400.000". Se usa SÓLO donde no hay que conciliar centavos.
 */
export function formatearPesosCortos(centavos) {
  const p = partirCentavos(centavos);
  return `${p.negativo ? '-' : ''}$ ${conPuntos(p.pesos)}`;
}

/**
 * Los km siempre con un decimal, como pide la guía de diseño: "1.180,0".
 * Llegan en décimos para no perder ese decimal en el camino.
 */
export function formatearKm(decimos) {
  const n = BigInt(decimos);
  const negativo = n < 0n;
  const abs = negativo ? -n : n;
  const entero = conPuntos((abs / 10n).toString());
  const decimal = (abs % 10n).toString();
  return `${negativo ? '-' : ''}${entero},${decimal}`;
}

/** Un porcentaje exacto, en diezmilésimos: 150000 -> "15,00 %" */
export function formatearPorcentaje(diezmilesimos) {
  const n = BigInt(diezmilesimos);
  const negativo = n < 0n;
  const abs = negativo ? -n : n;
  const entero = (abs / 10000n).toString();
  const decimal = (abs % 10000n).toString().padStart(4, '0').slice(0, 2);
  return `${negativo ? '-' : ''}${conPuntos(entero)},${decimal} %`;
}

/** Una fecha ISO como la escribe cualquiera acá: "28/08/2026". */
export function formatearFecha(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** "2026-08" -> "agosto de 2026" */
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
               'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export function formatearPeriodo(periodo) {
  const partes = /^(\d{4})-(\d{2})$/.exec(periodo ?? '');
  if (!partes) return periodo ?? '';
  const mes = MESES[Number(partes[2]) - 1];
  return mes ? `${mes} de ${partes[1]}` : periodo;
}
